import type { App } from "@teamworksdev/react";
import config, {
  customerFacingApps,
  getMergeDocumentUrl,
  getPersonStatusUrl,
  getPersonUpdateUrl,
  getUnmergeDocumentUrl,
} from "~/config";
import api, { getAuthHeaders } from "~/services/api";
import { DocumentStatus, type GetDocumentRequest, type GetDocumentsRequest } from "~/services/documents";
import { makeDuplicateProfileGroups } from "./makeDuplicateProfile";
import { makePartialMatchDetail, makePartialMatches } from "./makePartialMatch";
import { makeId, makePeople, makePerson } from "./makePerson";
import {
  type CreateUMAImpersonationGrantRequest,
  type DuplicateProfilesPage,
  type FindDuplicateProfilesParams,
  MatchUpsertReviewAction,
  type PartialMatchSearchParams,
  type Person,
  type ReviewEntry,
} from "./types";

export const activatePerson = async (documentId: string, app: App) =>
  api
    .post(
      getPersonStatusUrl(documentId, app),
      {
        status: DocumentStatus.active,
      },
      {
        headers: {
          "target-app": app,
        },
      },
    )
    .then(({ data }) => data);

export const deactivatePerson = async (documentId: string, app: App) =>
  api
    .post(
      getPersonStatusUrl(documentId, app),
      {
        status: DocumentStatus.inactive,
      },
      {
        headers: {
          "target-app": app,
        },
      },
    )
    .then(({ data }) => data);

export const linkPeople = async ({
  leftDocumentId,
  rightDocumentId,
}: {
  leftDocumentId: string;
  rightDocumentId: string;
}) =>
  api
    .post(getMergeDocumentUrl(leftDocumentId), {
      right: [rightDocumentId],
    })
    .then(() => leftDocumentId);

export const mergePeople = async ({
  leftDocumentId,
  rightDocumentIds,
}: {
  leftDocumentId: string;
  rightDocumentIds: string[];
}) =>
  api
    .post(getMergeDocumentUrl(leftDocumentId), {
      right: rightDocumentIds,
    })
    .then(() => leftDocumentId);

export const getPerson = async (documentId: string, req: Partial<GetDocumentRequest>) => {
  const body = {
    ...defaultPersonFields,
    ...req,
  };

  return api.post(`${config.api.documents.view}/${documentId}`, body).then(({ data }) => makePerson(data));
};

export const getPeople = async (req: Partial<GetDocumentsRequest>) => {
  const body = {
    ...req,
    view_rule: {
      ...defaultPersonFields,
      ...req.view_rule,
    },
    document_type: "person",
  };
  return api.post(config.api.documents.view, body).then(({ data }) => makePeople(data));
};

export const unlinkPeople = async (documentId: string, targetApp: App) => {
  return api
    .post(
      getUnmergeDocumentUrl(documentId),
      {
        target_app_slug: targetApp,
      },
      {
        headers: {
          "target-app": targetApp,
        },
      },
    )
    .then(({ data }) => makeId(data));
};

export const updatePerson = async ({ document, update }: { document: Person; update: Partial<Person> }) =>
  api
    .post(getPersonUpdateUrl(document.id), {
      upserts: {
        ...update,
      },
      invalidation_action: "LOG",
    })
    .then(({ data }) => data);

export const getPartialMatchesByOrgId = async (
  orgId: string,
  { limit = 10, offset = 0, search, order_by, apps, hub_athlete_status_ids, active_only }: PartialMatchSearchParams,
) => {
  const searchParam = search ? `&search=${search}` : "";
  const orderByParam = order_by ? `&order_by=${encodeURIComponent(`${order_by[0]}${order_by.split(":")[1]}`)}` : "";
  const appsParam = apps
    ? apps.reduce((acc, app) => {
        return `${acc}&apps=${app}`;
      }, "")
    : "";
  const hubAthleteStatusParam = hub_athlete_status_ids?.length
    ? hub_athlete_status_ids.reduce(
        (acc, statusId) => `${acc}&hub_athlete_status_ids=${encodeURIComponent(statusId)}`,
        "",
      )
    : "";
  const activeOnlyParam = active_only ? "&active_only=true" : "";
  return api
    .get(
      `${config.api.documents.partialMatchesByOrg}/${orgId}?limit=${limit}&offset=${offset}${searchParam}${orderByParam}${appsParam}${hubAthleteStatusParam}${activeOnlyParam}`,
    )
    .then(({ data }) => makePartialMatches(data));
};

export const getPartialMatchesByAppSlug = async (
  appSlug: string,
  {
    limit = 10,
    offset = 0,
    search,
    order_by,
    by_org_ids,
    hub_athlete_status_ids,
    active_only,
  }: PartialMatchSearchParams,
) => {
  const searchParam = search ? `&search=${search}` : "";
  const orderByParam = order_by ? `&order_by=${encodeURIComponent(`${order_by[0]}${order_by.split(":")[1]}`)}` : "";
  const orgsParam = by_org_ids?.length
    ? by_org_ids.reduce((acc, org) => `${acc}&by_org_ids=${encodeURIComponent(org)}`, "")
    : "";
  const hubAthleteStatusParam = hub_athlete_status_ids?.length
    ? hub_athlete_status_ids.reduce(
        (acc, statusId) => `${acc}&hub_athlete_status_ids=${encodeURIComponent(statusId)}`,
        "",
      )
    : "";
  const activeOnlyParam = active_only ? "&active_only=true" : "";

  return api
    .get(
      `${config.api.documents.partialMatchesByApp}/${appSlug}?limit=${limit}&offset=${offset}${searchParam}${orderByParam}${orgsParam}${hubAthleteStatusParam}${activeOnlyParam}`,
    )
    .then(({ data }) => makePartialMatches(data));
};

export const getPartialMatchCountsByOrg = async () =>
  api.get<Record<string, number>>(config.api.documents.partialMatchCountsByOrg).then(({ data }) => data);

export const getPartialMatchCountsByApp = async () =>
  api.get<Record<string, number>>(config.api.documents.partialMatchCountsByApp).then(({ data }) => data);

export const getPartialMatchDetail = async (partialMatchId: string) =>
  api
    .get(`${config.api.documents.partialMatchesRoot}/${partialMatchId}/detail`)
    .then(({ data }) => makePartialMatchDetail(data));

export const reviewPartialMatch = async (
  partialMatchId: string,
  documentIdToMergeInto: string | null,
  targetApp: string,
  action: MatchUpsertReviewAction,
) => {
  const payload: ReviewEntry = {
    partial_match_id: Number(partialMatchId),
    action,
  };

  if (action === MatchUpsertReviewAction.merge) {
    payload.merge_into_document_id = documentIdToMergeInto;
  }

  return api
    .post(`${config.api.documents.partialMatchesRoot}/${partialMatchId}/review`, payload, {
      headers: getAuthHeaders(targetApp),
    })
    .then(({ data }) => data);
};

export const bulkReviewPartialMatches = async (matches: ReviewEntry[]) =>
  api.post(config.api.documents.profileUpsertReview, matches).then(({ data }) => data);

export const getImpersonationToken = async (person: Person, targetApp: App) =>
  api
    .post<{ access_token: string }>(
      "/auth/token/impersonation",
      {
        document_id: person.id,
        target_app_slug: targetApp,
      },
      {
        headers: {
          "target-app": targetApp,
        },
      },
    )
    .then(({ data }) => data.access_token);

export const createUMAImpersonationGrant = async (documentId: string) => {
  const payload: CreateUMAImpersonationGrantRequest = { impersonated_person_document_id: documentId };
  return api.post<void>("/api/v1/impersonation-grants", payload);
};

export const triggerProfileEvent = async (documentId: string) =>
  api.post<void>(`/api/v1/profiles/${documentId}/trigger-event`).then(({ data }) => data);

export const findDuplicateProfiles = async (
  orgId: string,
  { perPage = 10, cursor, matchFields, app, search }: FindDuplicateProfilesParams,
): Promise<DuplicateProfilesPage> =>
  api
    .post(`${config.api.duplicateProfiles}/${orgId}/profiles/duplicates`, {
      per_page: perPage,
      cursor: cursor ?? undefined,
      match_fields: matchFields,
      app: app ?? undefined,
      search: search?.trim() || undefined,
    })
    .then(({ data }) => ({
      perPage: data.per_page,
      nextPage: data.next_page,
      currentPage: data.current_page,
      previousPage: data.previous_page,
      totalGroups: data.total_groups,
      items: makeDuplicateProfileGroups(data.items),
    }));

const makeAccessFields = () => {
  const access: Record<string, { src: string[]; default: boolean }> = {};

  customerFacingApps.forEach((app) => {
    access[`${app}HasWebAccess`] = {
      src: [`${app}.profile:hasWebAccess`],
      default: true,
    };
    access[`${app}HasMobileAccess`] = {
      src: [`${app}.profile:hasMobileAccess`],
      default: true,
    };
  });

  return access;
};

const makeProfileIds = () => {
  const profileIds: Record<
    string,
    {
      src: string[];
    }
  > = {};

  customerFacingApps.forEach((app) => {
    profileIds[`${app}ProfileId`] = {
      src: [`${app}.profile:profileId`],
    };
  });
  return profileIds;
};

const defaultPersonFields = {
  givenName: {
    src: ["axle.profile:givenName"],
  },
  preferredName: {
    src: ["axle.profile:preferredName"],
  },
  familyName: {
    src: ["axle.profile:familyName"],
  },
  email: {
    src: ["axle.profile:email"],
  },
  primaryPhone: {
    src: ["axle.profile:primaryPhone"],
  },
  birthDate: {
    src: ["axle.profile:birthDate"],
  },
  clientUniqueId: {
    src: ["axle.profile:clientUniqueId"],
  },
  imageUrl: {
    src: ["axle.profile:imageUrl"],
  },
  oktaUserId: {
    src: ["axle.profile:oktaUserId"],
  },
  activeWithoutLoginAccess: {
    src: ["hub.profile:activeWithoutLoginAccess"],
  },
  ...makeAccessFields(),
  ...makeProfileIds(),
};
